import { apiClient } from "./api-client";
import type {
  CreatePaysheetPayload,
  FinanceActivity,
  PaysheetDetail,
  PaysheetLineItem,
  PaysheetStatus,
  PaysheetSummary,
  UpdatePaysheetPayload
} from "../types/paysheet.types";

const parseAmount = (value: unknown): number => {
  if (value == null) {
    return 0;
  }
  if (typeof value === "number") {
    return value;
  }
  const parsed = Number(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const mapLineItem = (item: any): PaysheetLineItem => ({
  id: item.id,
  participantId: item.participantId ?? null,
  participantName: item.participantName,
  nationalId: item.nationalId ?? null,
  amount: parseAmount(item.amount),
  paymentMethod: item.paymentMethod,
  bankAccountNumber: item.bankAccountNumber ?? null,
  bankRoutingNumber: item.bankRoutingNumber ?? null,
  cooperativeAccountRef: item.cooperativeAccountRef ?? null,
  districtCode: item.districtCode ?? null,
  notes: item.notes ?? null
});

const mapSummary = (paysheet: any): PaysheetSummary => ({
  id: paysheet.id,
  referenceNumber: paysheet.referenceNumber,
  program: paysheet.program,
  status: paysheet.status,
  cycleName: paysheet.cycleName,
  disbursementDate: paysheet.disbursementDate,
  totalAmount: parseAmount(paysheet.totalAmount),
  createdAt: paysheet.createdAt,
  createdBy: paysheet.createdBy
});

const mapDetail = (paysheet: any): PaysheetDetail => ({
  ...mapSummary(paysheet),
  notes: paysheet.notes ?? null,
  submittedAt: paysheet.submittedAt ?? null,
  verifiedAt: paysheet.verifiedAt ?? null,
  authorizedAt: paysheet.authorizedAt ?? null,
  cancelledAt: paysheet.cancelledAt ?? null,
  approvals: paysheet.approvals ?? [],
  lineItems: Array.isArray(paysheet.lineItems)
    ? paysheet.lineItems.map(mapLineItem)
    : [],
  attachments: paysheet.attachments ?? [],
  activities: paysheet.activities ?? []
});

export interface PaysheetListFilters {
  status?: PaysheetStatus;
  query?: string;
}

export const paysheetApi = {
  async list(filters?: PaysheetListFilters): Promise<PaysheetSummary[]> {
    const { data } = await apiClient.get<{ status: string; data: any[] }>("/paysheets", {
      params: {
        status: filters?.status,
        q: filters?.query
      }
    });
    return (data.data ?? []).map(mapSummary);
  },

  async get(id: string): Promise<PaysheetDetail> {
    const { data } = await apiClient.get<{ status: string; data: any }>(`/paysheets/${id}`);
    return mapDetail(data.data);
  },

  async create(payload: CreatePaysheetPayload): Promise<PaysheetDetail> {
    const { data } = await apiClient.post<{ status: string; data: any }>("/paysheets", payload);
    return mapDetail(data.data);
  },

  async update(id: string, payload: UpdatePaysheetPayload): Promise<PaysheetDetail> {
    const { data } = await apiClient.patch<{ status: string; data: any }>(
      `/paysheets/${id}`,
      payload
    );
    return mapDetail(data.data);
  },

  async submit(id: string, comment?: string | null): Promise<PaysheetDetail> {
    const { data } = await apiClient.post<{ status: string; data: any }>(
      `/paysheets/${id}/submit`,
      { comment: comment ?? null }
    );
    return mapDetail(data.data);
  },

  async verify(id: string, approve: boolean, comment?: string | null): Promise<PaysheetDetail> {
    const { data } = await apiClient.post<{ status: string; data: any }>(
      `/paysheets/${id}/verify`,
      { approve, comment: comment ?? null }
    );
    return mapDetail(data.data);
  },

  async authorize(id: string, approve: boolean, comment?: string | null): Promise<PaysheetDetail> {
    const { data } = await apiClient.post<{ status: string; data: any }>(
      `/paysheets/${id}/authorize`,
      { approve, comment: comment ?? null }
    );
    return mapDetail(data.data);
  },

  async unauthorize(id: string, comment?: string | null): Promise<PaysheetDetail> {
    const { data } = await apiClient.post<{ status: string; data: any }>(
      `/paysheets/${id}/unauthorize`,
      { comment: comment ?? null }
    );
    return mapDetail(data.data);
  },

  async cancel(id: string, reason?: string | null): Promise<PaysheetDetail> {
    const { data } = await apiClient.post<{ status: string; data: any }>(
      `/paysheets/${id}/cancel`,
      { reason: reason ?? null }
    );
    return mapDetail(data.data);
  }
};

export interface DashboardFinanceSummary {
  paysheetStatusBreakdown: Array<{ status: PaysheetStatus; count: number }>;
  paysheetAuthorizedTotal: number;
  paysheetPendingApproval: number;
  recentFinanceActivities: FinanceActivity[];
}

export const financeSummaryApi = {
  async get(): Promise<DashboardFinanceSummary> {
    const { data } = await apiClient.get<{ status: string; data: any }>("/dashboard/stats");
    const dto = data.data ?? {};
    return {
      paysheetStatusBreakdown: dto.paysheetStatusBreakdown ?? [],
      paysheetAuthorizedTotal: Number(dto.paysheetAuthorizedTotal ?? 0),
      paysheetPendingApproval: dto.paysheetPendingApproval ?? 0,
      recentFinanceActivities: dto.recentFinanceActivities ?? []
    };
  }
};
